import type { IntegrityRun, IntegrityView } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, ShieldCheck, XCircle } from 'lucide-react';
import { api } from '../../../lib/api';
import { dateTime, plural } from '../../../lib/format';
import { useMe } from '../../../lib/session';
import { Button } from '../../../ui/button';
import { Badge, ErrorBox, Spinner } from '../../../ui/feedback';

const STATUS = {
  ok: { tone: 'success', text: 'Всё в порядке' },
  warn: { tone: 'warning', text: 'Есть замечания' },
  fail: { tone: 'danger', text: 'Есть ошибки' },
} as const;

const TRIGGER: Record<string, string> = {
  manual: 'по кнопке',
  daily: 'ночная проверка',
  revert: 'после отмены из журнала',
};

function RunCard({ run, title }: { run: IntegrityRun | null; title: string }) {
  if (!run)
    return (
      <section className="rounded-md border border-line px-4 py-3">
        <h3 className="text-[14px] font-semibold text-ink">{title}</h3>
        <p className="mt-1 text-[13px] text-ink-2">Проверки ещё не было — запустите её кнопкой.</p>
      </section>
    );
  const s = STATUS[run.status];
  return (
    <section className="flex flex-col gap-2 rounded-md border border-line px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-[14px] font-semibold text-ink">{title}</h3>
        <Badge tone={s.tone}>{s.text}</Badge>
      </div>
      <p className="text-[13px] text-ink-2">
        <span className="num">{dateTime(run.at)}</span>, {TRIGGER[run.trigger] ?? run.trigger} · проверок:{' '}
        {run.checks}
        {run.durationMs !== null
          ? ` · ${(Math.max(100, run.durationMs) / 1000).toFixed(1).replace('.', ',')} с`
          : ''}
      </p>
      {run.problems.length ? (
        <ul className="flex flex-col gap-2">
          {run.problems.map((p) => (
            <li key={`${p.code}-${p.title}`} className="flex gap-2 text-[13px]">
              {p.severity === 'fail' ? (
                <XCircle className="mt-0.5 size-4 shrink-0 text-danger" aria-label="Ошибка" />
              ) : (
                <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning" aria-label="Замечание" />
              )}
              <div className="min-w-0">
                <div className="font-medium text-ink">
                  {p.title}
                  {p.count !== null ? ` — ${p.count}` : ''}
                </div>
                {p.details ? <div className="[overflow-wrap:anywhere] text-ink-2">{p.details}</div> : null}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="flex items-center gap-1.5 text-[13px] text-success">
          <CheckCircle2 className="size-4" aria-hidden />
          Проблем не найдено
        </p>
      )}
    </section>
  );
}

/**
 * Проверка базы (НФ-12, ДС-11): данные компании — администратору; вся база, печати журнала и миграции —
 * владельцу портала. Ночью проверка идёт сама, после каждой отмены из журнала — тоже.
 */
export function IntegrityPanel() {
  const me = useMe();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['journal', 'integrity'],
    queryFn: () => api<IntegrityView>('/admin/journal/integrity'),
  });
  const run = useMutation({
    mutationFn: () => api<IntegrityRun>('/admin/journal/integrity/run', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['journal', 'integrity'] }),
  });
  const runAll = useMutation({
    mutationFn: () => api<IntegrityRun>('/platform/integrity/run', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['journal', 'integrity'] }),
  });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const v = q.data;
  return (
    <div className="flex flex-col gap-4">
      <p className="text-[14px] text-ink-2">
        Портал проверяет, что данные согласованы: отмена из журнала проходит пробный прогон и отменяет
        действия целиком, а после неё и каждую ночь база проверяется. Журнал хранится{' '}
        {plural(v.retentionMonths, 'месяц', 'месяца', 'месяцев')}, его нельзя изменить задним числом — каждые
        сутки он запечатывается.
      </p>
      <RunCard run={v.tenant} title="Данные компании" />
      <div className="flex flex-wrap gap-2">
        <Button
          onClick={() => run.mutate()}
          loading={run.isPending}
          icon={<ShieldCheck className="size-4" />}
        >
          Проверить данные компании
        </Button>
      </div>
      {run.error ? <ErrorBox error={run.error} /> : null}

      {me.user.isPlatformAdmin ? (
        <>
          <RunCard run={v.platform} title="Вся база (владелец портала)" />
          {v.seals ? (
            <section className="flex flex-col gap-1 rounded-md border border-line px-4 py-3 text-[13px]">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-[14px] font-semibold text-ink">Печати журнала</h3>
                {v.seals.broken ? (
                  <Badge tone="danger">не сходится: {v.seals.broken}</Badge>
                ) : (
                  <Badge tone="success">сходятся</Badge>
                )}
              </div>
              <p className="text-ink-2">
                Запечатано дней: {v.seals.sealedDays}
                {v.seals.lastDay ? `, последний — ${v.seals.lastDay}` : ''}. Печать — отпечаток всех изменений
                дня; если запись журнала поправить в базе, печать не сойдётся.
              </p>
            </section>
          ) : null}
          <div>
            <Button onClick={() => runAll.mutate()} loading={runAll.isPending}>
              Проверить всю базу
            </Button>
          </div>
          {runAll.error ? <ErrorBox error={runAll.error} /> : null}
        </>
      ) : null}

      <details className="text-[13px] text-ink-2">
        <summary className="inline-flex min-h-11 cursor-pointer items-center font-medium text-ink md:min-h-0">
          Что проверяется
        </summary>
        <ul className="mt-2 flex list-disc flex-col gap-1 pl-5">
          <li>
            Данные компании: остатки склада не уходят в минус; записи, восстановленные из журнала, на месте.
          </li>
          <li>
            Вся база: изоляция компаний на каждой таблице, журнал изменений подключён ко всем таблицам,
            разделы журнала на следующие месяцы созданы, ограничения и ключи базы проверены, нет ссылок между
            компаниями, миграции базы совпадают с версией портала, печати журнала сходятся.
          </li>
          <li>
            При отмене: запись не меняли в обход журнала, ключи и ограничения базы выполняются, связанные
            записи и итоги отменяются вместе, статусы и подписи меняются только действием в своём разделе.
          </li>
        </ul>
      </details>
    </div>
  );
}
